import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell, refreshModalTitle } from './modalDialogShell';

export interface ConfirmDialogOptions {
  message?: string;
  messageKey?: string;
  messageParams?: Record<string, unknown>;
  title?: string;
  titleKey?: string;
  confirmLabelKey?: string;
  cancelLabelKey?: string;
  confirmClassName?: string;
  helpTopic?: string;
}

export function showConfirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  return showModalDialog({ ...options, blocking: false });
}

export function showBlockingConfirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  return showModalDialog({ ...options, blocking: true });
}

/** Single-action dialog for reading long content (e.g. deletion reasons). */
export function showMessageDialog(options: {
  titleKey?: string;
  title?: string;
  message?: string;
  messageKey?: string;
  messageParams?: Record<string, unknown>;
  closeLabelKey?: string;
  helpTopic?: string;
}): Promise<void> {
  return new Promise((resolve) => {
    const hasTitle = Boolean(options.titleKey || options.title);
    const shell = createModalShell({
      titleKey: options.titleKey,
      title: options.title,
      titleId: 'message-dialog-title',
      helpTopic: options.helpTopic ?? (hasTitle ? dialogHelpTopic('confirm') : undefined),
      className: 'confirm-dialog confirm-dialog--message',
      role: 'dialog',
    });

    const message = document.createElement('p');
    message.className = 'confirm-dialog__message';
    message.id = 'message-dialog-message';
    shell.body.append(message);

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'button primary';
    closeButton.id = 'message-dialog-close-action';
    closeButton.innerHTML = `<span data-i18n="${options.closeLabelKey ?? 'common.close'}"></span>`;
    shell.actions.append(closeButton);

    const refreshLocale = () => {
      refreshModalTitle(shell, { titleKey: options.titleKey, title: options.title });
      message.textContent = options.messageKey
        ? t(options.messageKey, { ...options.messageParams, interpolation: { escapeValue: false } })
        : (options.message ?? '');
      applyI18n(shell.dialog);
    };

    refreshLocale();
    const unbindLocale = bindLocaleRefresh(refreshLocale);

    document.body.append(shell.backdrop);

    const finish = () => {
      unbindLocale();
      unbindDismiss();
      shell.backdrop.remove();
      resolve();
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: finish,
    });

    closeButton.addEventListener('click', finish);
    closeButton.focus();
  });
}

function showModalDialog(options: ConfirmDialogOptions & { blocking: boolean }): Promise<boolean> {
  return new Promise((resolve) => {
    const hasTitle = Boolean(options.titleKey || options.title);
    const shell = createModalShell({
      titleKey: options.titleKey,
      title: options.title,
      titleId: 'confirm-dialog-title',
      helpTopic: options.helpTopic ?? (hasTitle ? dialogHelpTopic('confirm') : undefined),
      closable: !options.blocking,
      className: 'confirm-dialog',
      role: 'alertdialog',
    });

    const message = document.createElement('p');
    message.className = 'confirm-dialog__message';
    message.id = 'confirm-dialog-message';
    shell.body.append(message);

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.id = 'confirm-dialog-cancel';
    cancelButton.innerHTML = `<span data-i18n="${options.cancelLabelKey ?? 'common.cancel'}"></span>`;

    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.className = `button ${options.confirmClassName ?? 'primary'}`;
    confirmButton.id = 'confirm-dialog-confirm';
    confirmButton.innerHTML = `<span data-i18n="${options.confirmLabelKey ?? 'common.confirm'}"></span>`;

    shell.actions.append(cancelButton, confirmButton);

    const refreshLocale = () => {
      refreshModalTitle(shell, { titleKey: options.titleKey, title: options.title });
      message.textContent = options.messageKey
        ? t(options.messageKey, { ...options.messageParams, interpolation: { escapeValue: false } })
        : (options.message ?? '');
      applyI18n(shell.dialog);
    };

    refreshLocale();
    const unbindLocale = bindLocaleRefresh(refreshLocale);

    document.body.append(shell.backdrop);

    const finish = (confirmed: boolean) => {
      unbindLocale();
      unbindDismiss();
      shell.backdrop.remove();
      resolve(confirmed);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => finish(false),
      blocking: options.blocking,
    });

    cancelButton.addEventListener('click', () => finish(false));
    confirmButton.addEventListener('click', () => finish(true));
    confirmButton.focus();
  });
}

export function confirmRevokeShare(): Promise<boolean> {
  return showConfirmDialog({
    titleKey: 'info.owner.revokeConfirmTitle',
    messageKey: 'info.owner.revokeConfirm',
    confirmLabelKey: 'info.owner.revoke',
    confirmClassName: 'primary',
    helpTopic: dialogHelpTopic('infoShareManage'),
  });
}

export function confirmDeleteSharedFolder(): Promise<boolean> {
  return showConfirmDialog({
    titleKey: 'files.owner.deleteConfirmTitle',
    messageKey: 'files.owner.deleteConfirm',
    confirmLabelKey: 'files.owner.delete',
    confirmClassName: 'primary',
    helpTopic: dialogHelpTopic('filesMove'),
  });
}
